import * as React from 'react';
import { BottomSheet as SpringBottomSheet } from 'react-spring-bottom-sheet';

import { InteractiveArea } from '@notino/react-styleguide';

import { useScrolledFromTop } from '@hooks/useScrolledFromTop';
import { cx } from '@linaria/core';

import {
  CloseButton,
  AbsoluteInteractiveArea,
  DrawerTitle,
  FloatingCloseButton,
  Header,
  StyledBottomSheet,
} from './styled';
import {
  DRAWER_CLOSE_BUTTON_AREA_CLASS,
  DrawerCloseType,
  useCloseType,
} from './useCloseType';

export type BottomSheetProps = Omit<
  React.ComponentProps<typeof SpringBottomSheet>,
  'onDismiss'
> & {
  onDismiss?: (closeType: DrawerCloseType) => void;
  ignoreBodySwipe?: boolean;
  noHeaderShadow?: boolean;
  noHeaderHandle?: boolean;
  /**
   * Renders a single circular close button (no title bar) pinned to the top of
   * the sheet. The content scrolls beneath it and the button gains a shadow
   * once scrolled. Takes precedence over `header`.
   */
  floatingCloseButton?: boolean;
  closeButtonAriaLabel?: string;
};

const BottomSheet = (props: BottomSheetProps) => {
  // hack for not being able to configure animation duration
  // should be fixed when this PR is merged https://github.com/stipsan/react-spring-bottom-sheet/pull/260
  const [shouldRender, setShouldRender] = React.useState(true);

  const { getCloseType } = useCloseType(props.open);
  const { sentinelRef, isScrolled } = useScrolledFromTop(
    Boolean(props.floatingCloseButton)
  );

  const handleDismiss = () => {
    if (!props.onDismiss) {
      return;
    }
    props.onDismiss(getCloseType());
    setTimeout(() => {
      setShouldRender(false);
      setTimeout(() => setShouldRender(true), 1);
    }, 500);
  };

  if (!shouldRender) {
    return null;
  }

  const ariaLabel =
    props['aria-label'] ||
    (typeof props.header === 'string' ? props.header : 'Bottom sheet dialog');

  // The floating variant renders no header, so the sheet stays a single white
  // surface the content scrolls within.
  const header =
    !props.floatingCloseButton && props.header ? (
      <Header>
        {/* dummy element to keep header centered */}
        <CloseButton style={{ visibility: 'hidden' }} />

        {typeof props.header === 'string' ? (
          <DrawerTitle>{props.header}</DrawerTitle>
        ) : (
          props.header
        )}

        <InteractiveArea
          className={DRAWER_CLOSE_BUTTON_AREA_CLASS}
          size="44px"
          onClick={handleDismiss}
        >
          <CloseButton />
        </InteractiveArea>
      </Header>
    ) : undefined;

  return (
    <StyledBottomSheet
      {...props}
      {...(props.ignoreBodySwipe && { 'data-body-scroll-lock-ignore': 'true' })}
      role="dialog"
      aria-modal={props.blocking !== false}
      aria-label={ariaLabel}
      initialFocusRef={props.initialFocusRef ?? false}
      $noHeaderHandle={props.noHeaderHandle}
      $noHeaderShadow={props.noHeaderShadow}
      onDismiss={handleDismiss}
      header={header}
    >
      {props.floatingCloseButton && (
        <>
          {/* Sentinel at the top of the scroll content; when it scrolls out of
              view the pinned button gains its shadow. */}
          <div ref={sentinelRef} aria-hidden="true" />

          <FloatingCloseButton
            type="button"
            className={cx(isScrolled && 'isScrolled')}
            onClick={handleDismiss}
            aria-label={props.closeButtonAriaLabel ?? 'Close'}
          >
            <CloseButton />
          </FloatingCloseButton>
        </>
      )}

      {!props.header && !props.floatingCloseButton && (
        <AbsoluteInteractiveArea
          className={DRAWER_CLOSE_BUTTON_AREA_CLASS}
          size="44px"
          onClick={handleDismiss}
        >
          <CloseButton />
        </AbsoluteInteractiveArea>
      )}
      {props.children}
    </StyledBottomSheet>
  );
};

export default BottomSheet;
